// src/pages/agents/AgentInteractTab.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext, useParams, Link as RouterLink } from 'react-router-dom'; // Added RouterLink
import {
  Box, Typography, TextField, Button, Paper, CircularProgress, Alert,
  Grid, Tabs, Tab, Divider, useTheme, IconButton, Tooltip,
  List, ListItem, ListItemText, ListItemIcon, Select, MenuItem, FormControl
} from '@mui/material';
import {
  Send as SendIcon, StopCircle as StopIcon, ClearAll as ClearAllIcon,
  Info as InfoIcon, Description as SystemPromptIcon, Memory as LLMIcon,
  Build as ToolsIcon, VpnKey as WatIcon, Dns as ApiBaseIcon,
  Code as CodeIcon, PlaylistPlay as LogIcon, Article as ContextIcon,
  Refresh as RefreshIcon, ChatBubbleOutline as ChatIcon, Payment as PaymentIcon // Added PaymentIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { agentTestChatService } from '../../api/agentTestChatService';
import { conversationService } from '../../api/conversationService';

import UserMessage from '../conversations/messageComponents/UserMessage';
import AssistantMessage from '../conversations/messageComponents/AssistantMessage';
import ToolCallMessage from '../conversations/messageComponents/ToolCallMessage';
import ToolReturnMessage from '../conversations/messageComponents/ToolReturnMessage';

const DEBUG_PANEL_TABS = [
  { label: 'Session Info', value: 'sessionInfo', icon: <InfoIcon /> },
  { label: 'Message Log', value: 'messageLog', icon: <LogIcon /> },
  { label: 'Tool Activity', value: 'toolActivity', icon: <CodeIcon /> },
];

const createConversationLabel = (convItem) => {
  let preview = `Conv. ${convItem.id.substring(0, 8)}`;
  if (convItem.first_user_message_preview) {
    const textPreview = String(convItem.first_user_message_preview).trim();
    if (textPreview) {
      preview = textPreview.length > 25 ? textPreview.substring(0, 22) + '...' : textPreview;
    }
  }
  const time = new Date(convItem.updated_at || convItem.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${preview} (${time})`;
};

const AgentInteractTab = () => {
  const { agent: agentFromContext } = useOutletContext();
  const { agentId } = useParams();
  const theme = useTheme();

  const [chatSessionConfig, setChatSessionConfig] = useState(null);
  const chatSessionConfigRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [currentInputValue, setCurrentInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [sessionError, setSessionError] = useState(null); // For general session/connection errors
  const [chatGatingError, setChatGatingError] = useState(null); // For subscription/credit errors

  const [debugLog, setDebugLog] = useState([]);
  const [toolActivityLog, setToolActivityLog] = useState([]);
  const [activeDebugTab, setActiveDebugTab] = useState(DEBUG_PANEL_TABS[0].value);

  const [activeConversationId, setActiveConversationId] = useState(null);
  const [availableConversations, setAvailableConversations] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingConversationList, setIsLoadingConversationList] = useState(false);

  const eventSourceRef = useRef(null);
  const messagesEndRef = useRef(null);
  const currentAssistantMessageParts = useRef([]);
  const currentAssistantMessageIsInterim = useRef(null);

  const logToDebugPanel = useCallback((type, data, source = "System") => {
    const timestamp = new Date().toISOString();
    setDebugLog(prev => [{ timestamp, type, data, source }, ...prev.slice(0, 199)]);
  }, []);

  const logToolActivity = useCallback((activity) => {
    const timestamp = new Date().toISOString();
    setToolActivityLog(prev => [{ timestamp, ...activity }, ...prev.slice(0, 99)]);
  }, []);

  const fetchAgentConversations = useCallback(async () => {
    if (!agentId) return;
    setIsLoadingConversationList(true);
    logToDebugPanel("Fetching Agent Conversations for Dropdown", { agentId }, "Frontend");
    try {
      const response = await conversationService.listConversations(
        agentId, 100, 0, null, 'updated_at', 'desc'
      );

      if (response.isError) {
        toast.warn(`Could not load conversation list: ${response.message}`);
        logToDebugPanel("Error Fetching Agent Conversations", { error: response.message }, "System");
        setAvailableConversations([]);
      } else {
        const formattedConversations = (response.items || []).map(conv => ({
          id: conv.id,
          label: createConversationLabel(conv),
        }));
        setAvailableConversations(formattedConversations);
        logToDebugPanel("Agent Conversations Loaded for Dropdown", { count: formattedConversations.length }, "Frontend");
      }
    } catch (err) {
      toast.warn(`Error fetching conversation list: ${err.message}`);
      logToDebugPanel("Exception Fetching Agent Conversations", { error: err.message }, "System");
      setAvailableConversations([]);
    } finally {
      setIsLoadingConversationList(false);
    }
  }, [agentId, logToDebugPanel]);

  const finalizeCurrentAssistantMessage = useCallback((finalContentOverride = null) => {
    if (currentAssistantMessageParts.current.length > 0 || finalContentOverride !== null) {
      const contentToSet = finalContentOverride !== null ? finalContentOverride : currentAssistantMessageParts.current.join('');
      if (contentToSet.trim() || finalContentOverride !== null) {
        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.type === 'assistant' && lastMsg.id.startsWith('assistant-streaming-')) {
            const updatedMessages = [...prev];
            updatedMessages[prev.length - 1] = {
              ...lastMsg,
              id: `assistant-${lastMsg.id.replace('assistant-streaming-', '')}-finalized`,
              content: contentToSet,
              isStreaming: false,
              isInterim: currentAssistantMessageIsInterim.current,
            };
            return updatedMessages;
          }
          const modelId = chatSessionConfigRef.current?.llm_model_id || 'N/A';
          return [...prev, {
            id: `assistant-${(currentAssistantMessageIsInterim.current ? 'interim-' : 'final-')}${Date.now()}-completed`,
            type: 'assistant',
            content: contentToSet,
            model: modelId,
            timestamp: new Date().toISOString(),
            isStreaming: false,
            isInterim: currentAssistantMessageIsInterim.current,
          }];
        });
      }
    }
    currentAssistantMessageParts.current = [];
    currentAssistantMessageIsInterim.current = null;
  }, []);

  const clearErrorsAndStreaming = useCallback(() => {
    setSessionError(null);
    setChatGatingError(null);
    setIsStreaming(false);
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const initializeSession = useCallback(async (showLoadingToast = false) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsLoadingSession(true);
    setSessionError(null);
    setChatGatingError(null); // Clear gating errors on new session
    setMessages([]);
    setDebugLog([]);
    setToolActivityLog([]);
    setActiveConversationId(null);
    setAvailableConversations([]);
    currentAssistantMessageParts.current = [];
    currentAssistantMessageIsInterim.current = null;
    if (showLoadingToast) toast.info("Initializing new test chat session...");

    try {
      const response = await agentTestChatService.initSession(agentId);
      if (response.isError) {
        throw new Error(response.message || "Failed to initialize session.");
      }
      setChatSessionConfig(response);
      chatSessionConfigRef.current = response;
      logToDebugPanel("Session Initialized (Test Chat)", response, "Backend");
      toast.success(`Test chat session ready for ${response.agent_name || 'Agent'}.`);

      await fetchAgentConversations();

    } catch (err) {
      console.error("Session initialization error:", err);
      const errMsg = err.message || "An unknown error occurred during session init.";
      setSessionError(errMsg); // Use general sessionError for init failures
      toast.error(`Session Error: ${errMsg}`);
      logToDebugPanel("Session Init Error", { message: errMsg, details: err }, "System");
    } finally {
      setIsLoadingSession(false);
    }
  }, [agentId, logToDebugPanel, fetchAgentConversations]);

  useEffect(() => {
    if (agentId) {
      initializeSession();
    }
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        logToDebugPanel("SSE Connection Closed (Component Unmount/Cleanup)", {}, "System");
      }
    };
  }, [agentId, initializeSession]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSseOpen = useCallback(() => {
    logToDebugPanel("SSE Connection Opened (Test Chat)", { url: eventSourceRef.current?.url }, "SSE Event");
    finalizeCurrentAssistantMessage();
  }, [logToDebugPanel, finalizeCurrentAssistantMessage]);

  const handleSseMessage = useCallback((event) => {
    logToDebugPanel("SSE Raw Data Received (Test Chat)", event.data, "SSE Event");
    try {
      const eventData = JSON.parse(event.data);
      logToDebugPanel("SSE Parsed Data (Test Chat)", eventData, "SSE Event");

      const messageIdSuffix = Date.now();

      if (eventData.conversation_id) {
        const newConvId = eventData.conversation_id;
        if (activeConversationId !== newConvId) {
          setActiveConversationId(newConvId);
          logToDebugPanel("Active Conversation ID Set", { conversation_id: newConvId }, "Frontend");
        }
        setAvailableConversations(prev => {
          const existing = prev.find(c => c.id === newConvId);
          if (existing) return prev;
          const firstUserMessage = messages.find(m => m.type === 'user');
          const newConvData = {
            id: newConvId,
            created_at: new Date().toISOString(),
            first_user_message_preview: firstUserMessage?.content || "New..."
          };
          const newConvLabel = createConversationLabel(newConvData);
          logToDebugPanel("New Conversation Added Dynamically to List", { conversation_id: newConvId, label: newConvLabel }, "Frontend");
          return [{ id: newConvId, label: newConvLabel }, ...prev];
        });
      }

      if (eventData.type === 'error') { // Handle gating errors first
        finalizeCurrentAssistantMessage();
        const errorMessage = eventData.error || eventData.error_details || "An error occurred in the stream.";
        logToDebugPanel("SSE Error Event (Test Chat)", { ...eventData, errorMessage }, "SSE Event");
        
        if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes("active subscription is required")) {
          setChatGatingError({ type: 'subscription', message: errorMessage });
        } else if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes("insufficient credits")) {
          setChatGatingError({ type: 'credits', message: errorMessage });
        } else {
          // For other errors, use the general sessionError or add to messages
           setMessages(prev => [...prev, {
            id: `error-${messageIdSuffix}`, type: 'error', content: `Stream Error: ${errorMessage}`,
            timestamp: eventData.timestamp || new Date().toISOString()
          }]);
          setSessionError(`Stream Error: ${errorMessage}`); // Or just display in chat
          toast.error(`Stream Error: ${errorMessage}`);
        }
        setIsStreaming(false);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        return; // Stop further processing for this error message
      }


      if (eventData.type === 'response_chunk' && eventData.content !== undefined) {
        const chunkIsInterim = eventData.is_interim === true;

        if (currentAssistantMessageIsInterim.current !== null && currentAssistantMessageIsInterim.current !== chunkIsInterim) {
          finalizeCurrentAssistantMessage();
        }

        currentAssistantMessageParts.current.push(eventData.content);
        currentAssistantMessageIsInterim.current = chunkIsInterim;

        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.type === 'assistant' &&
            lastMsg.id.startsWith('assistant-streaming-') &&
            lastMsg.isInterim === chunkIsInterim) {
            const updatedMessages = [...prev];
            updatedMessages[prev.length - 1] = {
              ...lastMsg,
              content: currentAssistantMessageParts.current.join(''),
              isStreaming: true,
            };
            return updatedMessages;
          } else {
            const modelId = chatSessionConfigRef.current?.llm_model_id || eventData.model || 'N/A';
            return [...prev, {
              id: `assistant-streaming-${(chunkIsInterim ? 'interim-' : 'final-')}${messageIdSuffix}`,
              type: 'assistant',
              content: currentAssistantMessageParts.current.join(''),
              model: modelId,
              timestamp: new Date().toISOString(),
              isStreaming: true,
              isInterim: chunkIsInterim,
            }];
          }
        });
      } else if (eventData.type === 'progress_update' && eventData.content?.tool_name) {
        finalizeCurrentAssistantMessage();

        const toolCallMsg = {
          id: `toolcall-${messageIdSuffix}-${eventData.content.tool_call_id || eventData.content.tool_name}`, type: 'tool-call',
          toolName: eventData.content.tool_name, args: eventData.content.args,
          toolCallId: eventData.content.tool_call_id,
          timestamp: eventData.timestamp || new Date().toISOString()
        };
        setMessages(prev => [...prev, toolCallMsg]);
        logToolActivity({ type: 'tool_call', data: eventData.content });

      } else if (eventData.type === 'tool_return' && eventData.tool_name) {
        finalizeCurrentAssistantMessage();

        const toolReturnMsg = {
          id: `toolreturn-${messageIdSuffix}-${eventData.tool_call_id || 'no-id'}`, type: 'tool-return',
          toolName: eventData.tool_name, content: eventData.output || eventData.content,
          toolCallId: eventData.tool_call_id, timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, toolReturnMsg]);
        logToolActivity({ type: 'tool_return', data: eventData });
      } else if (eventData.type === 'stream_end' || eventData.type === 'complete') {
        let finalContentForThisTurn = "";
        if (eventData.type === 'complete' && typeof eventData.final_response === 'string') {
          finalContentForThisTurn = eventData.final_response;
        }

        finalizeCurrentAssistantMessage(finalContentForThisTurn || null);

        logToDebugPanel("SSE Stream Ended/Completed (Test Chat)", eventData, "SSE Event");
        setIsStreaming(false);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
      }
      // Removed the 'error' type handling from here as it's now prioritized at the top of this function.
    } catch (e) {
      console.error("Error parsing SSE event data:", e, event.data);
      logToDebugPanel("SSE Data Parse Error (Test Chat)", { error: e.message, rawData: event.data }, "System");
    }
  }, [logToDebugPanel, logToolActivity, activeConversationId, finalizeCurrentAssistantMessage, messages]);

  const handleSseError = useCallback((errorDetails) => { // For EventSource's own onerror
    console.error("SSE Connection Error (Test Chat):", errorDetails);
    logToDebugPanel("SSE Connection Error (Test Chat)", { ...errorDetails, activeConversationId }, "SSE Event");

    finalizeCurrentAssistantMessage();
    let displayMessage = "";

    if (errorDetails.isConfigurationError) {
      displayMessage = errorDetails.message;
    } else if (errorDetails.type === 'abort') {
      displayMessage = 'Chat stream stopped by user.';
      toast.info(displayMessage); // User initiated, so info toast
    } else if (errorDetails.status === 422) { // Unprocessable Content
      let detailMsg = "Unprocessable Content. Check payload.";
      try {
        const body = errorDetails.originalError?.responseBody;
        if (body?.detail) {
          if (Array.isArray(body.detail)) {
            detailMsg = body.detail.map(d => `${d.loc?.join('.') || 'field'}: ${d.msg}`).join('; ');
          } else if (typeof body.detail === 'string') {
            detailMsg = body.detail;
          }
        }
      } catch (e) { /* ignore */ }
      displayMessage = `Connection failed: Unprocessable Content (${errorDetails.status}). ${detailMsg}`;
    } else if (errorDetails.status) { // Other HTTP errors
      displayMessage = `Connection failed: HTTP ${errorDetails.status}. ${errorDetails.message || ''}`;
    } else { // Generic connection errors
      displayMessage = errorDetails.message || "SSE connection closed unexpectedly or an error occurred.";
    }
    
    // Avoid setting sessionError if it's a user-initiated abort or if a specific gating error is already set
    if (errorDetails.type !== 'abort' && !chatGatingError) {
        setSessionError(displayMessage);
        toast.error(displayMessage, { autoClose: errorDetails.status === 422 ? 10000 : 5000 });
    }
    
    setIsStreaming(false);
    if (eventSourceRef.current) {
      eventSourceRef.current = null;
    }
  }, [logToDebugPanel, activeConversationId, finalizeCurrentAssistantMessage, chatGatingError]);


  const handleSendMessage = async () => {
    if (!currentInputValue.trim() || isStreaming || !chatSessionConfigRef.current || !chatSessionConfigRef.current.wat) {
      if (!chatSessionConfigRef.current || !chatSessionConfigRef.current.wat) {
        toast.warn("Session not ready or WAT missing. Try re-initializing.");
      }
      return;
    }

    // Clear previous errors when a new message is sent
    setSessionError(null);
    setChatGatingError(null);

    const userMessageContent = currentInputValue.trim();
    const userMessageForDisplay = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: userMessageContent,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessageForDisplay]);
    setCurrentInputValue('');
    setIsStreaming(true);

    const payloadForBackend = {
      user_message: userMessageContent,
      conversation_id: activeConversationId,
    };

    logToDebugPanel("SSE Request Body to /stream (Test Chat)", payloadForBackend, "Frontend");

    const currentConfig = chatSessionConfigRef.current;
    if (!currentConfig || !currentConfig.api_base_url || !currentConfig.wat || !agentId) {
      const errMsg = "Chat session configuration or Agent ID is incomplete. Cannot send message.";
      setSessionError(errMsg); // Use general sessionError for this config issue
      toast.error(errMsg);
      setIsStreaming(false);
      logToDebugPanel("Configuration Error", { chatSessionConfig: currentConfig, agentId, activeConversationId }, "System");
      return;
    }

    eventSourceRef.current = agentTestChatService.streamTestConversation(
      agentId,
      currentConfig.api_base_url,
      payloadForBackend,
      currentConfig.wat,
      handleSseMessage,
      handleSseError,
      handleSseOpen
    );
  };

  const handleStopStreaming = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      logToDebugPanel("SSE Stream Manually Stopped by User (Test Chat)", { activeConversationId }, "System");
      eventSourceRef.current = null; // Nullify after closing
    }
    finalizeCurrentAssistantMessage();
    setIsStreaming(false);
    // Don't clear chatGatingError here as the condition still exists
  };

  const handleClearChatMessages = () => {
    setMessages([]);
    currentAssistantMessageParts.current = [];
    currentAssistantMessageIsInterim.current = null;
    const currentConvLabel = activeConversationId
      ? (availableConversations.find(c => c.id === activeConversationId)?.label || `Conv ${activeConversationId.substring(0, 8)}...`)
      : 'new conversation';
    toast.info(`Messages cleared from view for ${currentConvLabel}.`);
    // Don't clear chatGatingError or sessionError as they might still be relevant context.
    // User might clear messages to try again, but the underlying error may persist.
  };

  const handleSelectConversation = async (conversationIdToLoad) => {
    if (isLoadingHistory || isStreaming || isLoadingConversationList) return;
    if (conversationIdToLoad === activeConversationId && conversationIdToLoad !== "") return;

    finalizeCurrentAssistantMessage();
    clearErrorsAndStreaming(); // Clear errors and stop streaming when switching conversations

    if (conversationIdToLoad === "") {
      setActiveConversationId(null);
      setMessages([]);
      currentAssistantMessageParts.current = [];
      currentAssistantMessageIsInterim.current = null;
      toast.info("Ready for a new conversation. Send a message to start.");
      logToDebugPanel("Switched to New Conversation Mode", {}, "Frontend");
      return;
    }

    setIsLoadingHistory(true);
    setMessages([]);
    currentAssistantMessageParts.current = [];
    currentAssistantMessageIsInterim.current = null;
    setActiveConversationId(conversationIdToLoad);
    logToDebugPanel("Conversation Switch Attempt", { conversationIdToLoad }, "Frontend");

    try {
      const currentConfig = chatSessionConfigRef.current;
      if (!currentConfig || !currentConfig.wat) {
        throw new Error("Session WAT is missing. Cannot fetch conversation history.");
      }
      const historyData = await agentTestChatService.getConversationHistory(
        agentId,
        conversationIdToLoad,
        currentConfig.wat
      );

      if (historyData.isError) {
        throw new Error(historyData.message || "Failed to fetch conversation history.");
      }

      const formattedMessages = (historyData.messages || []).map((msg, index) => ({
        ...msg,
        id: msg.id || `hist-${msg.type}-${msg.timestamp || Date.now()}-${index}`,
        isStreaming: false,
        isInterim: msg.type === 'assistant' ? (msg.is_interim === true) : undefined,
      }));

      setMessages(formattedMessages);
      const convLabel = availableConversations.find(c => c.id === conversationIdToLoad)?.label || `Conv ${conversationIdToLoad.substring(0, 8)}...`;
      toast.success(`Switched to conversation ${convLabel}`);
      logToDebugPanel("Conversation History Loaded", { conversationId: conversationIdToLoad, count: formattedMessages.length }, "Frontend");

    } catch (err) {
      console.error("Error fetching conversation history:", err);
      const convLabel = availableConversations.find(c => c.id === conversationIdToLoad)?.label || `Conv ${conversationIdToLoad.substring(0, 8)}...`;
      toast.error(`Error loading history for ${convLabel}: ${err.message}`);
      setSessionError(`Failed to load history for ${convLabel}: ${err.message}`); // Use general sessionError
      logToDebugPanel("Conversation History Load Error", { conversationId: conversationIdToLoad, error: err.message }, "System");
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const renderMessage = (msg) => {
    const messageStyle = msg.type === 'assistant' && msg.isInterim === true && !msg.isStreaming
      ? { opacity: 0.7, fontStyle: 'italic' }
      : {};
    switch (msg.type) {
      case 'user': return <UserMessage key={msg.id} {...msg} />;
      case 'assistant': return <AssistantMessage key={msg.id} {...msg} sx={messageStyle} />;
      case 'tool-call': return <ToolCallMessage key={msg.id} {...msg} />;
      case 'tool-return': return <ToolReturnMessage key={msg.id} {...msg} />;
      case 'error': return <Alert key={msg.id} severity="error" sx={{ my: 1, fontSize: '0.9rem' }}>{msg.content}</Alert>;
      default: return <Box key={msg.id || Math.random()} sx={{ my: 1, p: 1, bgcolor: 'grey.200' }}>Unknown: {JSON.stringify(msg)}</Box>;
    }
  };

  const renderSessionInfo = () => {
    const currentConfig = chatSessionConfig;
    if (!currentConfig) return <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>Session not initialized.</Typography>;
    const items = [
      { icon: <SystemPromptIcon />, label: "System Prompt", value: currentConfig.system_prompt_text || "Not Set", pre: true },
      { icon: <LLMIcon />, label: "LLM Model", value: currentConfig.llm_model_id || "N/A" },
      { icon: <ToolsIcon />, label: "Enabled Tools", value: currentConfig.enabled_tools?.join(', ') || "None" },
      { icon: <WatIcon />, label: "Session WAT", value: `${currentConfig.wat?.substring(0, 15)}... (exp: ${new Date(currentConfig.wat_expires_utc * 1000).toLocaleTimeString()})` },
      { icon: <ApiBaseIcon />, label: "API Base URL", value: currentConfig.api_base_url },
      ...(activeConversationId ? [{ icon: <ChatIcon />, label: "Active Conv. ID", value: activeConversationId }] : [])
    ];
    return (
      <List dense>
        {items.map(item => (
          <ListItem key={item.label} divider>
            <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.pre ? <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.8rem', maxHeight: 150, overflowY: 'auto', p: 0.5, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>{item.value}</Typography> : item.value}
              secondaryTypographyProps={{ component: 'div' }}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  const renderLogList = (logItems, emptyMsg = "No logs yet.") => {
    if (logItems.length === 0) return <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>{emptyMsg}</Typography>;
    return (
      <List dense sx={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
        {logItems.map((log, index) => (
          <ListItem key={index} divider sx={{ py: 0.5, display: 'block', '&:hover': { bgcolor: theme.palette.action.selected } }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
              {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 })} - [{log.source || 'N/A'}] - {log.type}
            </Typography>
            <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.75rem', bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100], p: 0.5, borderRadius: 1, mt: 0.25, fontFamily: 'monospace' }}>
              {typeof log.data === 'object' ? JSON.stringify(log.data, null, 2) : String(log.data)}
            </Typography>
          </ListItem>
        ))}
      </List>
    );
  };

  if (!agentFromContext && isLoadingSession && !chatSessionConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3, height: 200 }}>
        <CircularProgress /><Typography sx={{ ml: 1 }}>Loading agent context...</Typography>
      </Box>
    );
  }

  const isOverallLoading = isLoadingSession || isLoadingHistory || (isLoadingConversationList && !chatSessionConfig);
  const isDropdownDisabled = isLoadingSession || isStreaming || isLoadingHistory || isLoadingConversationList;
  const isInputDisabled = isStreaming || isLoadingSession || isLoadingHistory || isLoadingConversationList || !!sessionError || !!chatGatingError;


  return (
    <Grid container spacing={2} sx={{ height: 'calc(100vh - 220px)' /* Adjust based on your layout */ }}>
      <Grid item xs={12} md={7} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Paper sx={{ p: { xs: 1, sm: 1.5 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', flexGrow: 1, mr: 1 }}>
            <Typography variant="h6" component="h2" noWrap sx={{ maxWidth: { xs: '100px', sm: '180px', md: '220px' }, mr: 1, flexShrink: 0 }}>
              Test Chat: {chatSessionConfig?.agent_name || agentFromContext?.name || 'Agent'}
            </Typography>
            {chatSessionConfig && (
              <FormControl size="small" sx={{ minWidth: 180, maxWidth: 280, flexShrink: 1, position: 'relative' }} disabled={isDropdownDisabled}>
                {isLoadingConversationList && !activeConversationId && (
                  <CircularProgress size={20} sx={{ position: 'absolute', left: 'calc(50% - 10px)', top: 'calc(50% - 10px)', zIndex: 1 }} />
                )}
                <Select
                  value={activeConversationId || ""}
                  onChange={(e) => handleSelectConversation(e.target.value)}
                  displayEmpty
                  renderValue={(selectedValue) => {
                    if (isLoadingConversationList && selectedValue === "" && !availableConversations.length) return <em style={{ color: theme.palette.text.disabled }}>Loading list...</em>;
                    if (selectedValue === "") {
                      return <em style={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>New Conversation</em>;
                    }
                    const conv = availableConversations.find(c => c.id === selectedValue);
                    return conv ? conv.label : `Conv ${selectedValue.substring(0, 8)}...`;
                  }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                  sx={{ fontSize: '0.875rem', '.MuiSelect-select': { pr: isLoadingConversationList ? '32px' : '24px' } }}
                >
                  <MenuItem value="" sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
                    New Conversation
                  </MenuItem>
                  {availableConversations.map(conv => (
                    <MenuItem key={conv.id} value={conv.id} sx={{ fontSize: '0.875rem' }}>
                      {conv.label}
                    </MenuItem>
                  ))}
                  {availableConversations.length === 0 && !isLoadingConversationList && (
                    <MenuItem disabled sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>No existing conversations found.</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          </Box>
          <Tooltip title="Re-initialize Session (Clears Chat & Resets Conversations)">
            <span>
              <IconButton onClick={() => initializeSession(true)} disabled={isDropdownDisabled} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Paper>

        {isOverallLoading && (
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 2 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              {isLoadingSession ? "Initializing test session..."
                : isLoadingHistory ? "Loading conversation history..."
                  : "Loading conversation list..."}
            </Typography>
          </Box>
        )}

        {/* Display Gating Error */}
        {chatGatingError && !isOverallLoading && (
          <Alert
            severity="warning"
            sx={{ m: 1, flexShrink: 0 }}
            action={
              <Button
                color="inherit"
                size="small"
                component={RouterLink}
                to="/billing" // Phase 1: Placeholder link
                onClick={() => setChatGatingError(null)} // Optionally clear error on navigating away
                startIcon={<PaymentIcon />}
              >
                {chatGatingError.type === 'subscription' ? 'Manage Subscription' : 'Add Credits'}
              </Button>
            }
          >
            <strong>Action Required:</strong> {chatGatingError.message}
          </Alert>
        )}

        {/* Display General Session Error (if no specific gating error) */}
        {sessionError && !chatGatingError && !isOverallLoading && (
          <Alert severity="error" sx={{ m: 1, flexShrink: 0 }}>
            <strong>Session Error:</strong> {sessionError}
            <Button size="small" onClick={() => initializeSession(true)} sx={{ ml: 1, mt: { xs: 1, sm: 0 } }}>Retry Init</Button>
          </Alert>
        )}


        {!isOverallLoading && ( /* Chat area shows if not overall loading, errors are handled above */
          <>
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 0.5, sm: 1 }, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, mb: 1, bgcolor: theme.palette.background.paper }}>
              {messages.length === 0 && !chatGatingError && !sessionError && ( /* Only show 'send message' if no errors */
                <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                  {isLoadingConversationList && !activeConversationId ? "Loading conversation list..." :
                    activeConversationId ? `No messages in ${(availableConversations.find(c => c.id === activeConversationId)?.label || `Conv ${activeConversationId.substring(0, 8)}...`)}.`
                      : 'Send a message to start a new conversation.'}
                </Typography>
              )}
              {messages.map(renderMessage)}
              <div ref={messagesEndRef} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexShrink: 0, p: { xs: 0.5, sm: 1 } }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Type your message..."
                value={currentInputValue}
                onChange={(e) => setCurrentInputValue(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                disabled={isInputDisabled}
                multiline
                minRows={1}
                maxRows={5}
                size="small"
              />
              {isStreaming ? (
                <Button variant="contained" color="warning" onClick={handleStopStreaming} startIcon={<StopIcon />} sx={{ height: '100%', minWidth: 90 }}>
                  Stop
                </Button>
              ) : (
                <Button variant="contained" onClick={handleSendMessage} disabled={!currentInputValue.trim() || isInputDisabled} startIcon={<SendIcon />} sx={{ height: '100%', minWidth: 90 }}>
                  Send
                </Button>
              )}
              <Tooltip title="Clear messages from current view">
                <span>
                  <IconButton onClick={handleClearChatMessages} disabled={isInputDisabled || messages.length === 0}>
                    <ClearAllIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </>
        )}
      </Grid>

      <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Tabs
            value={activeDebugTab}
            onChange={(e, newValue) => setActiveDebugTab(newValue)}
            variant="fullWidth"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0, minHeight: 48 }}
          >
            {DEBUG_PANEL_TABS.map(tab => (
              <Tab key={tab.value} label={tab.label} value={tab.value} icon={tab.icon} iconPosition="start" sx={{ minHeight: 48, fontSize: '0.75rem', p: { xs: '6px 8px', sm: '6px 12px' } }} />
            ))}
          </Tabs>
          <Box sx={{ p: { xs: 1, sm: 1.5 }, flexGrow: 1, overflowY: 'auto', bgcolor: theme.palette.background.default }}>
            {activeDebugTab === 'sessionInfo' && renderSessionInfo()}
            {activeDebugTab === 'messageLog' && renderLogList(debugLog, "No system/SSE messages logged.")}
            {activeDebugTab === 'toolActivity' && renderLogList(toolActivityLog, "No tool activity logged.")}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default AgentInteractTab;